import {useMemo} from 'react';

import {addQueryToPath} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {ApplicationModel} from '../../models/api/applicationModel';
import {CompanyModel} from '../../models/api/companyModel';
import {ApiLocalApplication, LocalApplicationModel} from '../../models/api/localApplicationModel';
import {
  ApiSearchLocalApplications,
  SearchLocalApplicationsModel,
} from '../../models/api/searchLocalApplicationModel';
import {fetchApiAsync} from '../../services/http/http';
import {PaginationParameter} from '../helpers/resourceListPagination/resourceListPaginationOffset';
import {makeResourceListPaginationSearchOffset} from '../helpers/resourceListPagination/resourceListPaginationSearchOffset';
import {usePersistedListMutation, useResourceList} from '../helpers/resourceTableHooks';
import {useSettingsQuery} from '../helpers/useSettingsQuery';
import {useQueryDebounce} from './fetcherHelpers';
import {
  LocalApplicationListMutation,
  localApplicationListMutations,
  LocalApplicationListPageType,
} from './localApplicationsMutations';

/*
 * Consts.
 */

const localApplicationQueryKeyFactory = {
  all: ['applications'],
  list: (query: string) => [...localApplicationQueryKeyFactory.all, {query}],
  id: (applicationId: number) => ['localApplication', {id: applicationId}],
};

/*
 * Fetchers.
 */

export function useFetchLocalApplication(installedApplication?: ApplicationModel) {
  // Create the query key based in the local application id. If we do not have a local application
  // the key doesn't really matter since we will not make the call.
  const queryKey = installedApplication?.localApplicationId
    ? localApplicationQueryKeyFactory.id(installedApplication?.localApplicationId)
    : 'temp-local-application-key';

  const {data, asyncStatus, refetch} = useSettingsQuery(
    queryKey,
    async ({company, cancelToken}) => {
      if (!installedApplication?.localApplicationId) {
        return undefined;
      }
      return fetchApiAsync(
        ApiLocalApplication,
        `${company.url}/local_applications/${installedApplication.localApplicationId}`,
        {cancelToken},
      );
    },
    {
      enabled: Boolean(installedApplication?.localApplicationId),
    },
  );

  return {
    application: data,
    status: installedApplication?.localApplicationId ? asyncStatus : AsyncStatusesEnum.LOADED,
    refetch,
  };
}

export function useFetchLocalApplications(query: string) {
  const debouncedQuery = useQueryDebounce(query);

  const queryKey = localApplicationQueryKeyFactory.list(debouncedQuery);

  const applicationPagination = useMemo(
    () =>
      makeResourceListPaginationSearchOffset<LocalApplicationModel, SearchLocalApplicationsModel>({
        apiType: ApiSearchLocalApplications,
        makeSearchUrl: (page, company) => buildLocalApplicationsSearchUrl(company, debouncedQuery, page),
      }),
    [debouncedQuery],
  );

  const {items, loadMore, moreStatus, hasError, showErrorBanner} = useResourceList(
    queryKey,
    applicationPagination,
  );

  const {mutate, isLoading, isSuccess} = usePersistedListMutation<
    LocalApplicationListMutation,
    LocalApplicationListPageType,
    typeof localApplicationListMutations,
    Error
  >(localApplicationQueryKeyFactory.all, localApplicationListMutations, showErrorBanner, true);

  return {
    items,
    loadMore,
    moreStatus,
    hasError,
    mutate,
    mutateIsLoading: isLoading,
    mutateIsSuccess: isSuccess,
  };
}

/*
 * Helpers.
 */

function buildLocalApplicationsSearchUrl(
  company: CompanyModel,
  query: string,
  pagination: PaginationParameter,
) {
  const path = `${company.url}/search_local_applications/${encodeURIComponent(query.toLowerCase())}`;

  return addQueryToPath(path, pagination);
}
